"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/UI/Form";

import { Button } from "~/components/UI/Button";
import GeneralModal from "~/app/_components/GeneralModal";
import { Input } from "~/components/UI/Input";
import { Textarea } from "~/components/UI/Textarea";
import { postSupport } from "~/lib/api/support";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useMemo, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { isErrorValue } from "~/lib/api/fetch/errors";

const formDefaultValues = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  subject: z.string().min(2, {
    message: "Subject must be at least 2 characters.",
  }),
  message: z.string().min(2, {
    message: "Message must be at least 2 characters.",
  }),
});

type FormValues = z.infer<typeof formSchema>;

function SubmitButton(props: { form: UseFormReturn<FormValues> }) {
  const values = props.form.watch();

  const hasChanged = useMemo(() => {
    let key: keyof FormValues;
    for (key in formDefaultValues) {
      if (formDefaultValues[key] !== values[key]) {
        return true;
      }
    }

    return false;
  }, [values]);

  return (
    <Button
      type="submit"
      variant="secondary"
      size="sm"
      disabled={!hasChanged}
      className="uppercase tracking-widest"
    >
      Submit
    </Button>
  );
}

export default function ContactFormColumn() {
  //
  const [showPopup, setShowPopup] = useState(false);
  const [isError, setIsError] = useState(false);

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const result = await postSupport(values);

      const isError = isErrorValue(result);

      setIsError(isError);
      setShowPopup(true);

      if (!isError) {
        //reset form
        form.reset();
      }
    } catch (e) {
      setIsError(true);
      setShowPopup(true);
    }
  };

  return (
    <div className="md:sticky md:top-28 h-fit max-w-[400px] flex flex-col gap-6 items-center w-full">
      <header className="flex flex-col gap-2 max-w-[300px] mx-auto">
        <h2 className="text-3xl leading-tight font-minion-pro capitalize">
          Can&apos;t find what you&apos;re looking for?
        </h2>
        <p className="text-sm">
          Drop us a message and our team will reach out to you.
        </p>
      </header>

      {showPopup && (
        <GeneralModal
          title={isError ? "Submission Failed" : "Support Ticket Submitted."}
          description={
            isError
              ? "There was an error while submitting the ticket. Please try again later."
              : "Thank you for reaching out to us. Our team will connect with you within 48 hours."
          }
        >
          <Button
            variant="secondary"
            onClick={() => setShowPopup(false)}
            className="uppercase w-full"
          >
            ok
          </Button>
        </GeneralModal>
      )}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4 w-[300px] [&_button[type=submit]]:mt-2 [&_label]:ps-1.5 shadow-[0_0px_20px_-3px_hsla(var(--secondary)/_0.25)] p-8 rounded-md"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your full name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="name@email.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Subject</FormLabel>
                <FormControl>
                  <Input placeholder="Enter a Subject." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="How can we help you?"
                    {...field}
                    className="min-h-0 "
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <SubmitButton form={form} />
        </form>
      </Form>

      <footer className="mx-auto w-fit max-w-[240px]">
        <p className="text-sm leading-tight">
          You can also reach out to us at{" "}
          <a
            href="mailto:customercare@we.properties"
            target="_blank"
            rel="noopener noreferrer"
            className="text-secondary"
          >
            customercare@we.properties 
          </a>
          {" "} or call us on {" "}
          <a
            href="tel:800937767"
            target="_blank"
            rel="noopener noreferrer"
            className="text-secondary"
          >
            800 WEPROP (937767)
          </a>
        </p>
      </footer>
    </div>
  );
}
